import { useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  SITE_NAME,
  TWITTER_SITE,
  absUrl,
  toDescription,
} from "../lib/seo";

/**
 * public/index.html ships a full default meta block so link scrapers that
 * never run JavaScript (Slack, iMessage, LinkedIn, Facebook) still get a
 * sensible card for any URL. react-helmet only ever removes tags it owns, so
 * once <Seo> mounts those static tags would sit alongside the page-specific
 * ones — two canonicals, two descriptions, two og:titles. Google treats
 * conflicting canonicals as no canonical at all.
 *
 * So: drop the shell's copies the first time any <Seo> mounts. The raw HTML
 * keeps its fallback, the rendered DOM ends up with exactly one of each.
 */
const SHELL_SELECTORS = [
  'meta[name="description"]',
  'meta[name="keywords"]',
  'meta[name="robots"]',
  'meta[name="googlebot"]',
  'link[rel="canonical"]',
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
];

let shellStripped = false;

const stripStaticShellTags = () => {
  if (shellStripped || typeof document === "undefined") return;
  shellStripped = true;
  SHELL_SELECTORS.forEach((selector) => {
    document.head.querySelectorAll(selector).forEach((el) => {
      if (!el.hasAttribute("data-react-helmet")) el.remove();
    });
  });
};

/**
 * Per-page SEO head. Every public route renders one; private routes render
 * one with `noindex`.
 *
 * @param {string}  [title]        Page title, already suffixed by the caller.
 * @param {string}  [description]  Free text or HTML — clipped to 160 chars.
 * @param {string}  [path]         App path ("/work/foo") → canonical + og:url.
 * @param {string}  [image]        Absolute or app-relative social image.
 * @param {string}  [type]         Open Graph type. "article" for posts/cases.
 * @param {boolean} [noindex]      Emit noindex,nofollow instead of index,follow.
 * @param {string}  [keywords]     Comma-separated. Optional; low SEO value.
 * @param {object|object[]} [jsonLd] Schema.org node(s), emitted as an @graph.
 * @param {string}  [publishedTime] ISO date — article:published_time.
 * @param {string}  [modifiedTime]  ISO date — article:modified_time.
 */
const Seo = ({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
  keywords,
  jsonLd,
  publishedTime,
  modifiedTime,
}) => {
  useEffect(stripStaticShellTags, []);

  const pageTitle = title || DEFAULT_TITLE;
  const pageDescription = toDescription(description || DEFAULT_DESCRIPTION);
  const canonical = absUrl(path || "/");
  const ogImage = image ? absUrl(image) : DEFAULT_OG_IMAGE;

  const nodes = jsonLd ? [].concat(jsonLd).filter(Boolean) : [];
  const graph = nodes.length
    ? { "@context": "https://schema.org", "@graph": nodes }
    : null;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical is omitted on noindex routes — a canonical pointing at a
          page you've asked not to index sends the crawler mixed signals. */}
      {!noindex && <link rel="canonical" href={canonical} />}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />
      {!noindex && (
        <meta
          name="googlebot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={pageTitle} />
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_SITE} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={pageTitle} />

      {graph && (
        <script type="application/ld+json">{JSON.stringify(graph)}</script>
      )}
    </Helmet>
  );
};

/** BreadcrumbList node. `trail` is [{ name, path }] from root to current page. */
export const breadcrumbs = (trail) => ({
  "@type": "BreadcrumbList",
  itemListElement: trail.map((crumb, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: crumb.name,
    item: absUrl(crumb.path),
  })),
});

export default Seo;
